import React, { useMemo, useCallback } from "react";

import { useScores } from "~/features/clientLibrary/useScores";

import type { ScoreKey, CategoryKey } from "~/sdks/LocalContent/types";
import type { TransformedScoresMenuData } from "~/sdks/LocalContent/utils/transformScoresMenuData/types";
import type { ScoreItem } from "./utils/transformScoresAPIResponse/types";
import { transformScoresAPIResponse } from "./utils/transformScoresAPIResponse";

export function useScoreSelection({
  scoresMenu,
  lat,
  lng,
}: {
  scoresMenu: TransformedScoresMenuData;
  lat: number;
  lng: number;
}) {
  const [activeScore, setActiveScore] = React.useState<ScoreKey | null>(null);
  const [activeCategory, setActiveCategory] =
    React.useState<CategoryKey | null>(null);

  const { data, isFetching: isScoresFetching } = useScores({
    location: {
      lat,
      lng,
    },
  });

  const scoresData = useMemo<ScoreItem>(
    () => transformScoresAPIResponse(data, scoresMenu),
    [data, scoresMenu]
  );

  const activeCategoryData = useMemo(() => {
    if (!activeCategory || !scoresMenu) return null;

    const category = scoresMenu.categories.find(
      ([key]) => key === activeCategory
    );

    if (!category) return null;

    return category[1];
  }, [activeCategory, scoresMenu]);

  const handleSelectScore = useCallback(
    (scoreKey: ScoreKey | null) => () => {
      setActiveScore((prev) => (prev === scoreKey ? null : scoreKey));

      if (scoreKey === null) {
        setActiveCategory(null);
        return;
      }

      const categoryKey =
        scoresMenu.categories.find(([, value]) =>
          value.scores.some(([key]) => key === scoreKey)
        )?.[0] ?? null;

      setActiveCategory(categoryKey);
    },
    [setActiveScore, setActiveCategory, scoresMenu.categories]
  );

  const handleSelectCategory = useCallback(
    (categoryKey: CategoryKey | null) => () => {
      setActiveCategory((prev) => (prev === categoryKey ? null : categoryKey));

      if (categoryKey === null) {
        setActiveScore(null);
      }
    },
    [setActiveCategory]
  );

  return {
    isLoading: isScoresFetching,
    scoresData,
    activeScore,
    activeScoreData: activeScore === null ? undefined : scoresData[activeScore],
    activeCategory,
    activeCategoryData,
    handleSelectScore,
    handleSelectCategory,
  };
}
